import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import logo from "@/assets/logo_medium.png";
import { supabase } from "@/integrations/supabase/client";
import { onboardingCopy } from "@/lib/onboarding-copy";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useOnboardingProfile, ONBOARDING_PROFILE_QUERY_KEY } from "@/hooks/useOnboardingProfile";
import { useHealthAreas } from "@/hooks/useHealthAreas";
import { Step1ProfessionalRegistration } from "./steps/Step1ProfessionalRegistration";
import { Step2EmailValidation } from "./steps/Step2EmailValidation";
import { Step3DocumentUpload } from "./steps/Step3DocumentUpload";
import { OnboardingSkeleton } from "./OnboardingSkeleton";
import Step2Analyze from "@/pages/smartfile/Step2Analyze";
import { getSmartfileSession } from "@/services/smartfile";
import { useGlobalAnalysis } from "@/hooks/useGlobalAnalysis";

const stepTransition = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
};

const TOTAL_STEPS = 4;

export default function OnboardingNew() {
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const globalAnalysisMutation = useGlobalAnalysis();

  const {
    step,
    setStep,
    isAuthenticated,
    setIsAuthenticated,
    professionalForm,
    setProfessionalForm,
    sessionId,
    setSessionId,
    patient,
    setPatient,
    resolveStepFromProfile,
  } = useOnboarding();

  const [session, setSession] = useState<{ user: { id: string; email?: string } | null } | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const { data: profile, isLoading: profileLoading } = useOnboardingProfile(
    session?.user?.id
  );
  const { data: healthAreas = [], isLoading: healthAreasLoading } = useHealthAreas();

  const [isRegistering, setIsRegistering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setSessionChecked(true);
      if (s?.user) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setSessionChecked(true);
      setIsAuthenticated(!!s?.user);
      if (s?.user) {
        queryClient.invalidateQueries({ queryKey: ONBOARDING_PROFILE_QUERY_KEY });
      }
    });
    return () => subscription.unsubscribe();
  }, [setIsAuthenticated, queryClient]);

  useEffect(() => {
    const urlName = searchParams.get("name");
    const urlEmail = searchParams.get("email");
    const urlHealthAreaId = searchParams.get("healthAreaId");
    const state = location.state as {
      name?: string;
      fullName?: string;
      email?: string;
      healthAreaId?: string;
    } | null;

    // Verificar se há parâmetros na URL ou state para atualizar
    const hasUrlParams = urlName !== null || urlEmail !== null || urlHealthAreaId !== null;
    const hasStateParams = state && (state.name || state.fullName || state.email || state.healthAreaId);

    if (hasUrlParams || hasStateParams) {
      setProfessionalForm((prev) => {
        const updates: Partial<typeof prev> = {};
        
        // Priorizar valores da URL sobre state, e state sobre valores anteriores
        if (urlName !== null) {
          updates.name = urlName;
        } else if (state?.name || state?.fullName) {
          updates.name = state.name || state.fullName || prev.name;
        }
        
        if (urlEmail !== null) {
          updates.email = urlEmail;
        } else if (state?.email) {
          updates.email = state.email;
        }
        
        // healthAreaId: sempre usar da URL se presente, senão do state, senão manter anterior
        if (urlHealthAreaId !== null) {
          updates.healthAreaId = urlHealthAreaId;
        } else if (state?.healthAreaId !== undefined) {
          updates.healthAreaId = state.healthAreaId;
        }
        
        return { ...prev, ...updates };
      });
    }
  }, [searchParams, location.state, setProfessionalForm]);

  // Garantir que healthAreaId da URL seja aplicado quando healthAreas for carregado
  useEffect(() => {
    const urlHealthAreaId = searchParams.get("healthAreaId");
    const state = location.state as {
      healthAreaId?: string;
    } | null;
    
    const healthAreaId = urlHealthAreaId || state?.healthAreaId;
    
    // Se temos um healthAreaId da URL/state e as áreas foram carregadas, garantir que está definido
    if (healthAreaId && healthAreas.length > 0 && !healthAreasLoading) {
      // Verificar se o ID existe na lista de áreas
      const areaExists = healthAreas.some((area) => area.id === healthAreaId);
      
      if (areaExists) {
        setProfessionalForm((prev) => {
          // Só atualizar se ainda não estiver definido ou se for diferente
          if (prev.healthAreaId !== healthAreaId) {
            return { ...prev, healthAreaId };
          }
          return prev;
        });
      }
    }
  }, [healthAreas, healthAreasLoading, searchParams, location.state, setProfessionalForm]);

  useEffect(() => {
    if (!sessionChecked) return;

    if (!session) {
      if (step !== 1) setStep(1);
      return;
    }

    if (profile?.onboarding_completed) {
      nav("/app", { replace: true });
      return;
    }

    if (profileLoading) {
      return;
    }

    // Não sobrescrever se já estiver no step 4 com sessionId
    if (step === 4 && sessionId) {
      return;
    }

    const resolved = resolveStepFromProfile(profile ?? null, true);
    if (step !== resolved) {
      setStep(resolved);
    }
  }, [profile, profileLoading, session, sessionChecked, resolveStepFromProfile, setStep, nav, step, sessionId]);

  const showSkeleton = sessionChecked && !!session && profileLoading && step === null;
  const effectiveStep: 1 | 2 | 3 | 4 | null = showSkeleton ? null : (step ?? 1);

  const handleRegister = async (data: {
    name: string;
    email: string;
    password: string;
    healthAreaId: string;
  }) => {
    setIsRegistering(true);
    try {
      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify-email`,
          data: {
            full_name: data.name,
            health_area_id: data.healthAreaId,
          },
        },
      });

      if (error) {
        toast.error(error.message);
        return;
      }
      if (!authData.user) {
        toast.error("Erro ao criar usuário");
        return;
      }

      const accessToken =
        authData.session?.access_token ??
        (await supabase.auth.getSession()).data.session?.access_token;

      if (accessToken) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        await fetch(`${supabaseUrl}/functions/v1/send-confirmation-email`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            apikey: anonKey,
          },
          body: JSON.stringify({
            user_id: authData.user.id,
            email: data.email,
            name: data.name,
          }),
        });
      } else {
        toast.error(
          "Conta criada, mas não há sessão ativa para enviar o e-mail de confirmação. Faça login e use reenviar."
        );
      }

      localStorage.setItem("pending_email_confirmation", data.email);
      toast.success("Conta criada! Verifique seu e-mail para confirmar.");
      // Manter no onboarding e ir para o step 2 (validação de email)
      setStep(2);
    } catch (e) {
      console.error(e);
      toast.error(onboardingCopy.general.error);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleSaveHealthArea = async (data: { healthAreaId: string }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Sessão expirada. Faça login novamente.");
      nav("/auth");
      return;
    }

    setIsSaving(true);
    try {
      const selectedArea = healthAreas.find((a) => a.id === data.healthAreaId);
      const { error } = await supabase
        .from("profiles")
        .update({
          health_area_id: data.healthAreaId,
          health_area: selectedArea?.name ?? null,
        })
        .eq("id", user.id);

      if (error) throw error;

      toast.success("Área de saúde salva!");
      queryClient.invalidateQueries({ queryKey: ONBOARDING_PROFILE_QUERY_KEY });
      setStep(3);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao salvar área da saúde");
    } finally {
      setIsSaving(false);
    }
  };

  const handleResendEmail = async () => {
    const email =
      session?.user?.email ??
      localStorage.getItem("pending_email_confirmation") ??
      "";
    if (!email) {
      toast.error("E-mail não encontrado. Por favor, faça o cadastro novamente.");
      nav("/onboarding");
      return;
    }

    setIsResending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.email || !session.access_token) {
        toast.error("Sessão expirada. Faça login novamente.");
        nav("/auth");
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/send-confirmation-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: anonKey,
        },
        body: JSON.stringify({ user_id: session.user.id, email: session.user.email }),
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error ?? "Erro ao reenviar e-mail");
        return;
      }
      toast.success(onboardingCopy.confirm.resendSuccess);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao reenviar e-mail. Tente novamente.");
    } finally {
      setIsResending(false);
    }
  };

  const handleAlreadyConfirmed = async () => {
    if (!session?.user?.id) return;
    const { data } = await supabase
      .from("profiles")
      .select("email_confirmed")
      .eq("id", session.user.id)
      .single();
    if (data?.email_confirmed) {
      queryClient.invalidateQueries({ queryKey: ONBOARDING_PROFILE_QUERY_KEY });
      setStep(3);
    } else {
      toast.error(
        "E-mail ainda não confirmado. Verifique sua caixa de entrada e clique no link enviado."
      );
    }
  };

  const handleChangeEmail = async () => {
    if (!session?.user?.id) return;

    setIsChangingEmail(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-own-account');

      if (error) throw error;

      if (data?.error) throw new Error(data.error);

      localStorage.removeItem("pending_email_confirmation");
      setStep(1);
      setIsAuthenticated(false);
      setSession(null);

      await supabase.auth.signOut();
    } catch (error) {
      console.error("Erro ao deletar conta:", error);
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleSessionCreated = (id: string) => {
    setSessionId(id);
    setStep(4);
  };

  const handleComplete = async () => {
    if (!sessionId) return;

    const updatedSession = await getSmartfileSession(sessionId);
    const patientId = updatedSession.patient_id;
    if (!patientId) {
      toast.error("É necessário vincular um paciente antes de finalizar");
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase
      .from("profiles")
      .update({ onboarding_completed: true, onboarding_step: 4 })
      .eq("id", user.id);

    toast.info("🤖 Gerando análise global da IA...");
    try {
      await globalAnalysisMutation.mutateAsync({ patientId });
    } catch (e) {
      console.error(e);
      toast.warning("⚠️ Análise global será gerada na próxima visualização");
    }

    toast.success("🎉 Onboarding concluído com sucesso!");
    nav(`/app/pacientes/${patientId}`);
  };

  const progressWidth = effectiveStep ? ((effectiveStep - 1) / (TOTAL_STEPS - 1)) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-accent/10 to-background">
      <div className="max-w-5xl mx-auto py-12 px-4 space-y-8">
        <motion.header
          className="text-center mb-8 space-y-4"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <img src={logo} alt="Elinia" className="h-auto w-auto mx-auto mb-6" />
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-full px-4 py-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Configure em minutos</span>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Vamos começar configurando seu primeiro paciente e processando seu
            primeiro envio de dados clínicos.
          </p>
        </motion.header>

        <div className="max-w-4xl mx-auto mb-12 px-4">
          <div className="grid grid-cols-4 relative">
            {/* Track - linha entre as bolinhas (margin para não sobrepor) */}
            <div
              className="absolute top-5 left-[calc(12.5%+24px)] right-[calc(12.5%+24px)] h-0.5 bg-muted rounded-full overflow-hidden"
              aria-hidden
            >
              <motion.div
                className="h-full bg-gradient-to-r from-primary to-secondary rounded-full"
                initial={false}
                animate={{ width: `${progressWidth}%` }}
                transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              />
            </div>

            {[
              { num: 1, label: "Cadastro\nprofissional" },
              { num: 2, label: "Validação\ne-mail" },
              { num: 3, label: "Envio\ndocumento" },
              { num: 4, label: "IA\nAnálise" },
            ].map(({ num, label }) => (
              <div
                key={num}
                className="flex flex-col items-center gap-2 relative z-10"
              >
                <div
                  className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold transition-all duration-300 bg-background ${
                    effectiveStep && effectiveStep >= num
                      ? "bg-gradient-to-br from-primary to-primary-glow text-white shadow-lg shadow-primary/25"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {effectiveStep && effectiveStep > num ? (
                    <CheckCircle2 className="h-5 w-5" />
                  ) : (
                    num
                  )}
                </div>
                <span className="text-[10px] font-medium text-center leading-tight whitespace-pre-line">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {effectiveStep === null && (
            <motion.div
              key="skeleton"
              {...stepTransition}
              className="min-h-[400px]"
            >
              <OnboardingSkeleton />
            </motion.div>
          )}

          {effectiveStep === 1 && (
            <motion.div key="step1" {...stepTransition}>
              <Step1ProfessionalRegistration
            isAuthenticated={isAuthenticated}
            formData={professionalForm}
            onFormChange={(partial) =>
              setProfessionalForm((prev) => ({ ...prev, ...partial }))
            }
            healthAreas={healthAreas}
            onRegister={handleRegister}
            onSaveHealthArea={handleSaveHealthArea}
            isRegistering={isRegistering}
            isSaving={isSaving}
            onLoginClick={() => nav("/auth")}
            onBackClick={() => nav("/")}
              />
            </motion.div>
          )}

          {effectiveStep === 2 && (
            <motion.div key="step2" {...stepTransition}>
              <Step2EmailValidation
            email={
              session?.user?.email ??
              localStorage.getItem("pending_email_confirmation") ??
              ""
            }
            onResendEmail={handleResendEmail}
            onAlreadyConfirmed={handleAlreadyConfirmed}
            onChangeEmail={handleChangeEmail}
            isResending={isResending}
            isChangingEmail={isChangingEmail}
              />
            </motion.div>
          )}

          {effectiveStep === 3 && (
            <motion.div key="step3" {...stepTransition}>
              <Step3DocumentUpload
            sessionId={sessionId}
            patientId={patient?.id ?? null}
            onSessionCreated={handleSessionCreated}
            onPatientLinked={setPatient}
            onBack={() => setStep(1)}
            healthAreaName={healthAreas.find(
              (a) => a.id === professionalForm.healthAreaId
            )?.name}
              />
            </motion.div>
          )}

          {effectiveStep === 4 && sessionId && (
            <motion.div key="step4" {...stepTransition}>
              <section className="bg-white border-2 rounded-2xl p-2 shadow-2xl space-y-6 max-w-7xl mx-auto">
                <Step2Analyze
              sessionId={sessionId}
              hideDiscard
              isOnboarding
              onBack={() => {
                setSessionId(null);
                setStep(3);
              }}
              onComplete={handleComplete}
                />
              </section>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

